import React, { useState } from 'react';
import { Plus, RotateCcw, X, Info } from 'lucide-react';
import { getPersistedSizes, savePersistedSizes } from '../utils/sizes';
import { useToast } from './Toast';

const DEFAULT_SIZES = ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'Único'];

export const SizeManager: React.FC = () => {
  const toast = useToast();
  const [sizes, setSizes] = useState<string[]>(() => getPersistedSizes());
  const [newSize, setNewSize] = useState('');

  const handleAddSize = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanSize = newSize.trim();
    
    if (!cleanSize) {
      toast.error({ title: 'Error', description: 'El talle no puede estar vacío.' });
      return;
    }

    if (sizes.includes(cleanSize)) {
      toast.error({ title: 'Error', description: `El talle "${cleanSize}" ya existe.` });
      return;
    }

    const updatedSizes = [...sizes, cleanSize];
    setSizes(updatedSizes);
    savePersistedSizes(updatedSizes);
    setNewSize('');
    toast.success({ title: 'Talle agregado', description: `Se agregó el talle "${cleanSize}" exitosamente.` });
  };

  const handleDeleteSize = (sizeToDelete: string) => {
    if (window.confirm(`¿Estás seguro de que querés eliminar el talle "${sizeToDelete}"?`)) {
      const updatedSizes = sizes.filter(s => s !== sizeToDelete);
      setSizes(updatedSizes);
      savePersistedSizes(updatedSizes);
      toast.success({ title: 'Talle eliminado', description: `Se eliminó el talle "${sizeToDelete}".` });
    }
  };

  const handleResetDefaults = () => {
    if (window.confirm('¿Restablecer los talles por defecto? Esto sobrescribirá tus talles actuales.')) {
      setSizes(DEFAULT_SIZES);
      savePersistedSizes(DEFAULT_SIZES);
      toast.info({ title: 'Talles restablecidos', description: 'Se volvieron a cargar los talles estándar.' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center bg-page-surface p-6 rounded-2xl border border-page-border gap-4">
        <div>
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-widest">Configuración</span>
          <h2 className="font-heading font-bold text-2xl text-ink mt-0.5">Talles Disponibles</h2>
        </div>
        <div>
          <button
            onClick={handleResetDefaults}
            className="flex items-center gap-2 bg-white text-ink-muted rounded-xl hover:bg-page-surface transition-all border border-page-border px-5 py-3 text-sm font-semibold"
            title="Restablecer predeterminados"
          >
            <RotateCcw className="w-4 h-4" /> Restablecer por defecto
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-page-border shadow-sm p-6 space-y-4 h-fit">
          <h3 className="font-heading font-bold text-base text-ink pb-3 border-b border-page-border">
            Agregar Nuevo Talle
          </h3>
          <form onSubmit={handleAddSize} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                Nombre del Talle / Medida
              </label>
              <input
                type="text"
                value={newSize}
                onChange={e => setNewSize(e.target.value)}
                placeholder="Ej: 38, XL, Talle único"
                className="w-full bg-white border border-page-border rounded-xl px-4 py-2.5 text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors text-sm"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-hover text-white font-bold py-2.5 rounded-xl shadow-sm shadow-brand-glow transition-all text-sm"
            >
              <Plus className="w-4 h-4" /> Agregar Talle
            </button>
          </form>

          <div className="flex gap-2.5 p-3.5 bg-page-surface border border-page-border rounded-xl text-ink-muted text-xs leading-relaxed">
            <Info className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
            <span>Los talles agregados aquí aparecerán de forma inmediata en el formulario de creación y edición de productos.</span>
          </div>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-page-border shadow-sm p-6 space-y-4">
          <h3 className="font-heading font-bold text-base text-ink pb-3 border-b border-page-border">
            Talles Activos ({sizes.length})
          </h3>

          {sizes.length === 0 ? (
            <div className="text-center py-12 text-ink-faint">
              <p className="text-sm">No hay talles definidos. Agrega uno o restablece los valores predeterminados.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {sizes.map(size => (
                <div
                  key={size}
                  className="flex items-center gap-1.5 pl-3.5 pr-2 py-1.5 bg-page-surface hover:bg-brand-surface border border-page-border hover:border-brand-border rounded-xl text-sm font-semibold text-ink transition-all group"
                >
                  <span>{size}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteSize(size)}
                    className="p-1 text-ink-faint hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                    title={`Eliminar talle ${size}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
